import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { tenantApi } from '../services/api';
import toast from 'react-hot-toast';

const s = {
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', marginBottom: 24 },
  card: { background: '#fff', borderRadius: 10, padding: 32, marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  label: { fontSize: 13, fontWeight: 600, color: '#333', display: 'block', marginBottom: 6 },
  input: { padding: '10px 14px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, width: '100%', boxSizing: 'border-box', marginBottom: 16 },
  btn: { padding: '10px 24px', background: '#7c83fd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  logoPreview: { width: 80, height: 80, borderRadius: 10, objectFit: 'contain', border: '2px solid #eee', padding: 4, marginBottom: 12 },
  logoPlaceholder: { width: 80, height: 80, borderRadius: 10, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, marginBottom: 12 },
  uploadArea: { border: '2px dashed #ddd', borderRadius: 8, padding: 24, textAlign: 'center', cursor: 'pointer', marginBottom: 16 },
};

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [companyName, setCompanyName] = useState(user?.tenantName || '');
  const [saving, setSaving] = useState(false);

  const logoSrc = preview || (user?.logoBase64
    ? `data:${user.logoContentType || 'image/png'};base64,${user.logoBase64}`
    : null);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/svg+xml'].includes(file.type)) {
      toast.error('Please upload PNG, JPG, or SVG');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2MB');
      return;
    }

    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      await tenantApi.updateLogo(user.tenantId, file);
      // persist logo in user context so it survives refresh
      const reader = new FileReader();
      reader.onload = (ev) => {
        const base64 = ev.target.result.split(',')[1];
        updateUser({ logoBase64: base64, logoContentType: file.type });
      };
      reader.readAsDataURL(file);
      toast.success('Logo updated successfully.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload logo');
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleNameUpdate = async () => {
    if (!companyName.trim()) return;
    setSaving(true);
    try {
      await tenantApi.update(user.tenantId, { name: companyName });
      updateUser({ tenantName: companyName });
      toast.success('Company name updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 style={s.title}>Settings</h1>

      <div style={s.card}>
        <h3 style={{ fontWeight: 600, marginBottom: 20 }}>Company Logo</h3>
        {logoSrc ? (
          <img src={logoSrc} alt="Company logo" style={s.logoPreview} />
        ) : (
          <div style={s.logoPlaceholder}>🏢</div>
        )}
        <div style={s.uploadArea} onClick={() => fileRef.current?.click()}>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml"
            style={{ display: 'none' }} onChange={handleLogoUpload} />
          <div style={{ fontSize: 24, marginBottom: 8 }}>📤</div>
          <div style={{ fontSize: 14, color: '#666' }}>
            {uploading ? 'Uploading...' : 'Click to upload logo (PNG, JPG, SVG — max 2MB)'}
          </div>
        </div>
      </div>

      <div style={s.card}>
        <h3 style={{ fontWeight: 600, marginBottom: 20 }}>Company Details</h3>
        <label style={s.label}>Company Name</label>
        <input style={s.input} value={companyName} onChange={e => setCompanyName(e.target.value)} />
        <label style={s.label}>Tenant ID</label>
        <input style={{ ...s.input, background: '#f9fafb', color: '#888' }} value={user?.tenantId || ''} disabled />
        <label style={s.label}>Plan</label>
        <input style={{ ...s.input, background: '#f9fafb', color: '#888' }} value={user?.plan || 'BASIC'} disabled />
        <button style={s.btn} onClick={handleNameUpdate} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
